// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Card from 'sail/Card';
import {Heading, Body, Text} from 'sail/Text';
import {UnstyledLink} from 'sail/UnstyledLink';
import {InlineLink} from 'sail/InlineLink';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useAccount} from 'docs/data';
import {DeveloperCommunityYoutubeSVG} from './DeveloperCommunityYoutubeSVG';
import './DeveloperCommunity.scss';

const messages = defineMessages({
  devCommunity: {
    id: 'docsApp.components.devCommunity.devCommunity',
    description: 'Title for developer community section',
    defaultMessage: 'Developer community',
  },
  youtube: {
    id: 'docsApp.components.devCommunity.youtube',
    description: 'Link to tutorials on youtube',
    defaultMessage: 'YouTube tutorials',
  },
  watchYoutube: {
    id: 'docsApp.components.devCommunity.watchYoutube',
    description: 'Subheading for youtube link',
    defaultMessage: 'Watch developer tutorials on our YouTube channel.',
  },
  devToolsTitle: {
    id: 'docsApp.components.devCommunity.devToolsTitle',
    description: 'Developer tools title',
    defaultMessage: 'Developer tools',
  },
  devToolsBody: {
    id: 'docsApp.components.devCommunity.devToolsBody',
    description: 'Developer tools body',
    defaultMessage:
      'Sign up for the developer newsletter to get highlights and updates.',
  },
  twitterTitle: {
    id: 'docsApp.components.devCommunity.twitterTitle',
    description: 'Twitter updates title',
    defaultMessage: 'Twitter updates',
  },
  twitterBody: {
    id: 'docsApp.components.devCommunity.twitterBody',
    description: 'Twitter updates body',
    defaultMessage:
      'Follow us on Twitter to get updates and join our community.',
  },
  discordTitle: {
    id: 'docsApp.components.devCommunity.discordTitle',
    description: 'Discord chat title',
    defaultMessage: 'Developer chat on Discord',
  },
  discordBody: {
    id: 'docsApp.components.devCommunity.discordBody',
    description: 'Discord chat body',
    defaultMessage:
      'Chat live with other developers on the official Stripe Discord.',
  },
});

type DeveloperCommunityProps = {|
  children: React.Node,
|};

export const DeveloperCommunity = ({children}: DeveloperCommunityProps) => {
  const {account} = useAccount();

  const youtubeBoxPadding = {
    top: 32,
    left: 32,
    right: 32,
    bottom: account.flags.dev_community_link_kill_switch ? 32 : 0,
  };

  return (
    <Box margin={{top: 48}}>
      <Text color="dark" size={20} weight="bold">
        <FormattedMessage {...messages.devCommunity} />
      </Text>
      <Box className="DeveloperCommunity" margin={{top: 20}}>
        <Box>
          <Card shadow="keyline" style={{height: '100%'}}>
            <Box
              padding={youtubeBoxPadding}
              flex={{direction: 'column'}}
              style={{height: '100%'}}
            >
              <Heading>
                <InlineLink href="https://www.youtube.com/stripedevelopers">
                  <FormattedMessage {...messages.youtube} />
                </InlineLink>
              </Heading>
              <Body>
                <FormattedMessage {...messages.watchYoutube} />
              </Body>
              <UnstyledLink href="https://www.youtube.com/stripedevelopers">
                <Box margin={{top: 16}}>
                  <DeveloperCommunityYoutubeSVG />
                </Box>
              </UnstyledLink>
            </Box>
          </Card>
        </Box>
        <Box>
          <Card shadow="keyline">
            <Box
              padding={{top: 32, left: 32, right: 32}}
              flex={{direction: 'column'}}
            >
              <ul>
                <li className="DeveloperCommunity-email-link">
                  <Heading>
                    <InlineLink href="https://stripe.dev/#developer-digest">
                      <FormattedMessage {...messages.devToolsTitle} />
                    </InlineLink>
                  </Heading>
                  <Body display="block">
                    <FormattedMessage {...messages.devToolsBody} />
                  </Body>
                </li>
                <li className="DeveloperCommunity-community-link">
                  <Heading>
                    <InlineLink href="https://twitter.com/stripe">
                      <FormattedMessage {...messages.twitterTitle} />
                    </InlineLink>
                  </Heading>
                  <Body display="block">
                    <FormattedMessage {...messages.twitterBody} />
                  </Body>
                </li>
                {!account.flags.dev_community_link_kill_switch &&
                  account.flags.docs_show_dev_community_link && (
                    <li className="DeveloperCommunity-community-link">
                      <Heading>
                        <InlineLink href="https://stripe.com/go/developer-chat">
                          <FormattedMessage {...messages.discordTitle} />
                        </InlineLink>
                      </Heading>
                      <Body display="block">
                        <FormattedMessage {...messages.discordBody} />
                      </Body>
                    </li>
                  )}
              </ul>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
